/*
 * Common form helpers
 */
export default angular.module('eventix.common.directives.formHelpers',[])
    .directive('addHiddenOption', function() {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:addHiddenOption
         * @restrict attribute
         * @description
         * Adds an item to an ngOptions array if the currently selected ngModel value
         * is not included.
         *
         * This is useful for (re-)populating form fields where a select box might
         * contain a "secret" value that is otherwise unavailable.
         *
         * Supports non-dynamic ngOptions in the formats:
         * ```
         * label for item in array
         * select as label for item in array
         * select as label (...group by|disable when...) for item in array
         * ```
         */
        return {
            restrict: 'A',
            link: function(scope, el, attrs) {
                /*
                 * For this trick to work, we need to parse the ngOptions expression to collect
                 * information about the source array, and what items look like.
                 *                     select          label?         itemName      array
                 *                     11111           22222           33333        44444 */
                let optionRegex = /^\s*(\S+)\s*(?:as\s+(\S+)\s*)?.*for\s+(\S+)\s+in\s+(\S+)\s*$/;
                let matches = optionRegex.exec(attrs.ngOptions || attrs.txOptions);
                if(!matches)
                    return;
                let sourceArray = [];
                let modelValue = null;
                scope.$watch(matches[4], arr => {
                    sourceArray = arr;
                    addHiddenOption();
                });
                scope.$watch(attrs.ngModel, model => {
                    modelValue = model;
                    addHiddenOption();
                });

                function addHiddenOption() {
                    if(_.isNil(modelValue))
                        return;
                    let exists = _.find(sourceArray, item => {
                        /*
                         * Create a mock scope and set item under itemName
                         *
                         * We use these mock scopes so that LoDash can use
                         * ngOption segments to get/set values
                         */
                        let localScope = {};
                        _.set(localScope, matches[3], item);
                        return _.get(localScope, matches[1]) === modelValue;
                    });
                    if(exists)
                        return;
                    // Create a new item in a mock scope
                    let localScope = {};
                    _.set(localScope, matches[3], {});
                    if(matches[2])
                        _.set(localScope, matches[2], modelValue);
                    _.set(localScope, matches[1], modelValue);
                    sourceArray.push(localScope[matches[3]]);
                }
            }
        };
    })
    .directive('disableInputs', function() {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:disableInputs
         * @restrict element/attribute
         * @description
         * Place a simple watch on a variable that will disable all a selection of child elements if the value is truthy.
         *
         * Only affects `input, select, button, .disable-inputs`
         * @param {String} disableInputs A scope attribute to be watched
         */
        return {
            restrict: 'AE',
            link: function(scope, el, attr) {
                var inputs = el.find('input,select,button,.disable-inputs');
                scope.$watch(attr.disableInputs, function(value) {
                    if(value)
                        inputs.attr('disabled', 'disabled');
                    else
                        inputs.removeAttr('disabled');
                });
            }
        };
    })
    .directive('inputGroupAddon', function($log) {
        return {
            restrict: 'C',
            link: function(scope, el, attr) {
                angular.element(el).on('click', function() {
                    try {
                        angular.element(el).parent().find('input')[0].focus();
                    } catch (e) {
                        $log.error('No input sibling found');
                    }
                });
            }
        };
    })
    .directive('localNumber', function($filter, Locales) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:localNumber
         * @restrict attribute
         * @requires eventix.common.filters:localNumber
         * @requires eventix.common.services.Locales
         * @requires ngModel
         * @description
         * Attach this directive to an input that's supposed to represent a
         * decimal. It'll properly format the values with the right decimal
         * and thousands seperator.
         *
         * @example <pre>
         * <input type="text" local-number ng-model="vm.price" /> // 1000.50 wil be formatted as 1.000,50
         * </pre>
         */
        return {
            restrict: 'A',
            require: '?ngModel',
            link: function(scope, el, attr, ngModelCtrl) {
                if(!ngModelCtrl) return;
                ngModelCtrl.$parsers.unshift(function(value) {
                    if(typeof value === 'string') {
                        value = value.replace(/([^\d.,]|[.,](?=[^.,]*\.))/,'');
                        value = value.replace(',','.');
                    }
                    return value;
                });
                ngModelCtrl.$render = render;
                el.on('blur', render);

                function render() {
                    el.val($filter('localNumber')(ngModelCtrl.$modelValue, Locales.selected));
                }
            }
        };
    })
    .directive('numberInput', function($filter, Locales) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:numberInput
         * @restrict attribute
         * @requires eventix.common.filters:localNumber
         * @requires eventix.common.services.Locales
         * @requires ngModel
         * @description
         * Attach this directive to an input that's supposed to represent a
         * number. It'll parse strange characters and make sure the model data
         * is valid.
         *
         * @example <pre>
         * <input type="text" number-input ng-model="vm.price" />
         * </pre>
         */
        return {
            restrict: 'A',
            require: '?ngModel',
            link: function(scope, el, attr, ngModelCtrl) {
                if(!ngModelCtrl) return;
                ngModelCtrl.$parsers.unshift(function(value) {
                    if(typeof value === 'string') {
                        value = value.replace(/([^\d.,]|[.,](?=[^.,]*[.,]))/g,'');
                        value = value.replace(',','.');
                    }

                    return parseFloat(value);
                });
                ngModelCtrl.$render = render;
                el.on('blur', render);

                function render() {
                    el.val($filter('localNumber')(ngModelCtrl.$modelValue));
                }
            }
        };
    })
    .directive('currencyInput', function($filter, Locales, Currencies) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:currencyInput
         * @restrict attribute
         * @requires eventix.common.filters:formatCurrency
         * @requires eventix.common.services.Locales
         * @requires eventix.common.services.Currencies
         * @requires ngModel
         * @description
         * Attach this directive to an input that's supposed to represent a
         * currency. It'll properly format the values and make sure the model
         * value is in the smallest denomination (cents).
         *
         * @param {String} currencyInput The target currency.
         *
         * @example <pre>
         * <input type="text" currency-input="EUR" ng-model="vm.price" />
         * </pre>
         */
        return {
            restrict: 'A',
            require: '?ngModel',
            link: function(scope, el, attr, ngModelCtrl) {
                if(!ngModelCtrl) return;

                let currency = 'EUR';

                attr.$observe('currencyInput', code => {
                    if(/^[A-Z]{3}$/.test(code))
                        currency = code;
                    render();
                });

                ngModelCtrl.$parsers.unshift(value => Currencies.parseCurrency(value, currency));

                ngModelCtrl.$render = render;

                el.on('blur', render);

                function render() {
                    let value = parseFloat(ngModelCtrl.$modelValue);

                    if(isNaN(value))
                        el.val('');
                    else if(value === 0)
                        el.val(0);
                    else
                        el.val($filter('formatCurrency')(value, currency, Locales.selected));
                }
            }
        };
    })
    .directive('formHelp', function($log) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:formHelp
         * @restrict attribute
         * @description
         * DEPRECATED - Use `formEl` instead
         * Display help messages alongside form inputs
         * Message and form input must share a common `.row`
         *
         * @example
         * <pre>
         * <div class="row">
         *     <form ng-submit="vm.submit()" form-help>
         *         <input name="username" ng-model="vm.username />
         *         <div class="form-help" name="other">Non-input element with form help</div>
         *     </form>
         *     <div class="col-help">
         *         <span class="help" id="username">Username help text</span>
         *         <span class="help" id="other">Help with non-input element</span>
         *     </div>
         * </div>
         * </pre>
         *
         * @param {String} formHelp The DOM selector query for the desired help message
         */
        return {
            restrict: 'A',
            link: function(scope,el,attr) {
                var parentOffsetElement = $('.col-help').parents('.row').eq(0);
                if(!parentOffsetElement.length) {
                    $log.debug('Form help not found or incorrectly formatted');
                    return;
                }

                function show() {
                    var position = $(this).offset();
                    var formPosition = parentOffsetElement.offset();
                    var selector = '.col-help #' + $(this).attr('name');
                    $(parentOffsetElement).find(selector).addClass('active').css({
                        top: position.top - formPosition.top - 3
                    });
                }

                function hide() {
                    var selector = '.col-help #' + $(this).attr('name');
                    $(selector).removeClass('active');
                }

                if(el[0].tagName === 'FORM')
                    el = $(el).find('input, select, .formHelp');

                $(el).on('mouseenter.formHelp', show);
                $(el).on('focus.formHelp','input,select', show);
                $(el).on('mouseleave.formHelp', hide);
                $(el).on('blur.formHelp', 'input,select', hide);
                scope.$on('$destroy', () => $(el).off('.formHelp'));
            }
        };
    })
    .directive('formError', function() {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:formError
         * @restrict element
         * @description
         * Display form input errors
         *
         * @example
         * <pre>
         * <form-error errors="vm.errors"></form-error>
         * </pre>
         *
         * @param {String[]} errors Array of error messages
         */
        return {
            restrict: 'E',
            template: '<div class="alert alert-warning alert-input" ng-show="errors.length">\n' +
                '    <div ng-repeat="error in errors track by $index" ng-bind-html="error | capitalize | toHtml"></div>' +
                '</div>',
            scope: { errors: '=' }
        };
    })
    .directive('formGroup', function($translate, $timeout) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:formGroup
         * @restrict element
         * @description
         * DEPRECATED, use `formEl` instead
         *
         * Unify form element DOM using a directive
         *
         * @param {String} label Label, optionally a angular.translate.string
         * @param {Object} [translateValues] Key-values object for angular-translate interpolation
         *
         * @example
         * <pre>
         * <form-group label="Username">
         *     <input type="text" ng-model="vm.username" class="form-control"/>
         * </form-group>
         * </pre>
         */
        return {
            restrict: 'E',
            transclude: true,
            scope: { label: '@', values: '=translateValues'},
            link: function(scope, el, attrs) {
                let label = $translate.instant(scope.label, scope.values);
                let input = _.first(el.find('input[type!=checkbox][type!=radio]'));
                $timeout(() => {
                    scope.for = el.find('input,select,textarea').eq(0).attr('name');
                });
                if(input && _.isEmpty(input.placeholder))
                    $translate(scope.label, scope.values).then(translation => input.setAttribute('placeholder', translation));
                el.find('input,textarea').on('blur', blur);
                el.find('select').on('change input', blur);
                function blur(event) {
                    let $input = angular.element(event.target);
                    let model = $input.attr('name') || $input.attr('ng-model');
                    if(!model || !model.length) return;
                    model = model.split('.').slice(-1).join('');
                    scope.$emit('input-blur', model);
                }
            },
            template:
                `<div class="form-group">
    <label class="col-sm-3 hidden-xs control-label" for="{{for}}">{{ label | translate:values | capitalize }}</label>
    <div class="col-sm-9" ng-transclude>
    </div>
</div>`
        };

    })

    /**
     * @ngdoc directive
     * @name eventix.common.directives:toggleEl
     * @restrict element
     * @description
     * Provides a toggle element with ngModel bindings
     *
     * Replaces toggleInput
     *
     * @param {String} labelOn Label for "On" state
     * @param {String} labelOff Label for "Off" state
     * @param {Any} [valueOn=true] Value for "On" state
     * @param {Any} [valueOff=false] Value for "Off" state
     * @param {Function} [onToggle=Function(toggled)]
     * Callback on toggling state, can cancel the switch if it returns a rejecting promise
     */
    .component('toggleEl', {
        require: {
            ngModel: 'ngModel'
        },
        bindings: {
            labelOff: '@',
            labelOn: '@',
            valueOn: '@',
            valueOff: '@',
            onToggle: '&',
            name: '@'
        },
        template: `<label class="visible-xs" ng-show="$ctrl.mobileLabel" translate="{{ $ctrl.mobileLabel }}"></label>
        <label class="toggle-input"
          ng-class="{ ticked: $ctrl.model }"
          label-off="{{$ctrl.labelOff}}"
          label-on="{{$ctrl.labelOn}}"
          ng-click="$ctrl.fire()">
  <input type="checkbox" ng-model="$ctrl.model" name="{{$ctrl.name}}">
   <div class="tick"></div>
</label>`,
        controller: function($scope, $element, $log, $q) {
            const $ctrl = this;
            $ctrl.$postLink = function() {
                _.defaults($ctrl, {
                    labelOn: '',
                    labelOff: '',
                    valueOn: true,
                    valueOff: false,
                    onToggle: angular.noop
                });
                $scope.$watch('$ctrl.ngModel.$modelValue', m => $ctrl.model = m == $ctrl.valueOn); // eslint-disable-line
                $scope.$watch('$ctrl.model', m => $ctrl.ngModel.$setViewValue(m ? $ctrl.valueOn : $ctrl.valueOff));
                let parentLabel = $element.parent('ng-transclude').parents('form-el,form-group').attr('label');
                if(parentLabel && parentLabel.length)
                    $ctrl.mobileLabel = parentLabel;
            };
            $ctrl.fire = _.debounce(() => {
                $q.resolve($ctrl.onToggle({ toggled: $ctrl.model }))
                    .catch(err => {
                        $ctrl.model = !$ctrl.model;
                        throw err;
                    });
            }, 100);
        }
    })
    .directive('toggleInput', function($timeout) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:toggleInput
         * @restrict element
         * @description
         * DEPRECATED - Use toggleEl instead
         * A toggle that can be used as a checkbox
         *
         * @param {Boolean} [model] The model that'll reflect checkbox state
         * @param {Function} [checked] A callback to determine initial toggle state
         * @param {String} [labelOff=off] Text for the `false` state
         * @param {String} [labelOn=on] Text for the `true` state
         *
         * @example
         * ```
         * <toggle-input model="ticketsAvailable"></toggle-input>
         * ```
         */
        return {
            restrict: 'E',
            template: `<label class="toggle-input" ng-class="{ ticked: model }"
      label-off="{{labelOff}}"
      label-on="{{labelOn}}">
  <input type="checkbox" ng-model="model" name="{{name}}" value="{{value}}">
   <div class="tick"></div>
</label>`,
            scope: {labelOff: '@', labelOn: '@', model: '=', value: '@', checked: '&', name: '@'},
            link: function(scope, el, attr) {
                _.defaults(scope, {
                    labelOn: 'on',
                    labelOff: 'off'
                });
                el.find('input').on('click', ev => ev.stopPropagation());
                if (attr.checked) {
                    scope.$watch(() => scope.checked(), checked => {
                        if (checked !== scope.model)
                            $timeout(() => scope.model = checked);
                    });
                }
            }
        };
    })
    .directive('urlFormat', function() {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:urlFormat
         * @restrict attribute
         * @description
         * Augment an input field that auto-prepends `http://` if its not there
         */
        return {
            restrict: 'A',
            require: '?ngModel',
            link: function(scope, el, attr, ngModelCtrl) {
                if(!ngModelCtrl) return;
                ngModelCtrl.$overrideModelOptions({ updateOn: 'blur' });
                ngModelCtrl.$parsers.unshift(function(value) {
                    if(_.isString(value) && value.indexOf('http') !== 0) {
                        value = 'http://' + value;
                        el.val(value);
                    }
                    return value;
                });
            }
        };
    })
    /**
     * @ngdoc directive
     * @name eventix.common.directives:currencyPicker
     * @restrict element
     * @description
     * A simple currency picker
     *
     * @param {String} ngModel Where the currency code should be stored
     */
    .component('currencyPicker', {
        template: `<tx-select
            tx-options="c.code as c.label for c in $ctrl.currencies"
            btn-class="form-control"
            no-purge
            add-hidden-option
            class="block"
            placeholder="{{'common.action.selectOne' | translate:{item: 'currency'} }}"
            ng-model="$ctrl.currency">
    </tx-select>`,
        controller: function($scope, Currencies, $attrs, $parse) {
            const $ctrl = this;

            $ctrl.$postLink = function() {
                var ngModel = $parse($attrs.ngModel);
                $ctrl.currencies = Currencies;

                $scope.$watch(
                    () => ngModel($scope.$parent),
                    currency => $ctrl.currency = currency
                );
                $scope.$watch(
                    '$ctrl.currency',
                    currency => ngModel.assign($scope.$parent, currency)
                );
            };
        }
    })
    /**
     * @ngdoc directive
     * @name eventix.common.directives:localePicker
     * @restrict element
     * @description
     * A simple locale picker
     *
     * @param {String} ngModel Where the locale code should be stored
     */
    .component('localePicker', {
        template: `<tx-select
                tx-options="l.code as l.label for l in $ctrl.locales"
                btn-class="form-control"
                class="block"
                placeholder="{{'common.action.selectOne' | translate:{item: 'locale'} }}"
                ng-model="$ctrl.locale">
        </tx-select>`,
        controller: function($scope, Locales, $parse, $attrs) {
            const $ctrl = this;
            $ctrl.$postLink = function() {
                var ngModel = $parse($attrs.ngModel);
                $ctrl.locales = Locales;
                $scope.$watch(
                    () => ngModel($scope.$parent),
                    locale => $ctrl.locale = locale
                );
                $scope.$watch(
                    '$ctrl.locale',
                    locale => ngModel.assign($scope.$parent, locale)
                );
            };
        }
    })
    /**
     * @ngdoc directive
     * @name eventix.common.directives:saveBtn
     * @restrict element
     * @description
     * A save button with (animated) icons for normal and busy states
     *
     * The element transcludes, but the default text is a translation of `common.action.save`
     *
     * @param {Boolean} [busy] Whether the button should be disabled and icon should be a spinner
     * @param {String} [icon='fa-save'] The icon to show in default state
     * @param {String} [btnClass='btn-info'] The icon to show in default state
     */
    .component('saveBtn', {
        bindings: { busy: '<', icon: '@', btnClass: '@' },
        transclude: true,
        template: `<button type="submit" class="btn" ng-class="$ctrl.btnClass || 'btn-info'" ng-disabled="$ctrl.busy">
            <i class="fa" ng-class="$ctrl.icon || 'fa-save'" ng-hide="$ctrl.busy"></i>
            <i class="fa fa-spin fa-spinner" ng-show="$ctrl.busy"></i>
            <ng-transclude>
                <translate>common.action.save</translate>
            </ng-transclude>
        </button>`
    })
    .directive('passwordField', function() {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:passwordField
         * @restrict element
         * @description
         * A password field with button that reveals input
         *
         * @param {Boolean} [model] The model that'll reflect user input
         *
         * @example
         * ```
         * <password-field model="vm.user.password"></password-field>
         * ```
         */
        return {
            restrict: 'E',
            scope: { model: '=', name: '@' },
            template: `<div class="input-group">
    <input class="form-control" ng-model="model" ng-attr-type="{{showPassword ? 'text':'password'}}" name="{{ name }}">
    <div class="input-group-btn">
        <button type="button" class="btn btn-default" ng-click="showPassword = !showPassword"><i class="fa" ng-class="showPassword ? 'fa-eye' : 'fa-eye-slash'"></i></button>
    </div>
</div>`
        };
    }).name;
